import React, { useContext, useEffect, useState, useRef, useMemo } from 'react'
import styled from 'styled-components'
import { useOutletContext } from 'react-router-dom'
import { MoreHorizontal } from 'lucide-react'
import ApiService from '../../../../services/ApiService'
import { OccurrencesOutletType } from '..'
import { Occurrence } from './types'
import ModalContext from '../../../../contexts/ModalContext'
import { OccurrenceContext } from '../../../../contexts/OccurrenceContext'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'
import ActionsDropdown from './ActionsDropdown'
import ConfirmationModal from '../../../../components/ModalConfirmation'
import { Filter } from '../../../../components/SubHeaderFilter/types'
import LoadMoreButton from '../../../../components/LoadMoreButton'

import {
    GlobalTable,
    HeaderLine,
    Th,
    Tr,
    Td,
    TdButtons,
} from '../../../../components/styles/table-style'

const TableWrapper = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    width: 100%;
    overflow: auto;
`

interface OccurrencesTableProps {
    search?: string
    appliedFilters?: Filter[]
}

const getOccurrenceValue = (occurrence: Occurrence, key: string): any => {
    switch (key) {
        case 'companyName':
            return occurrence.companyName || occurrence.contactName || ''
        case 'Stage':
            return occurrence.Stage?.name || ''
        default:
            return (occurrence as any)[key]
    }
}

const matchesFilter = (occurrence: Occurrence, filter: Filter): boolean => {
    const raw = getOccurrenceValue(occurrence, filter.key)

    if (filter.fieldType === 'boolean') {
        const boolVal = Boolean(raw)
        return boolVal === filter.value
    }

    const value = String(raw ?? '').toLowerCase()
    const filterVal = String(filter.value ?? '').toLowerCase()

    switch (filter.filterType) {
        case 'Contém':
            return value.includes(filterVal)
        case 'Igual a':
            return value === filterVal
        case 'Diferente de':
            return value !== filterVal
        case 'Começa com':
            return value.startsWith(filterVal)
        case 'Termina com':
            return value.endsWith(filterVal)
        case 'Maior que':
            return Number(raw) > Number(filter.value)
        case 'Menor que':
            return Number(raw) < Number(filter.value)
        case 'Maior ou igual':
            return Number(raw) >= Number(filter.value)
        case 'Menor ou igual':
            return Number(raw) <= Number(filter.value)
        default:
            return value.includes(filterVal)
    }
}

const truncateStyle: React.CSSProperties = {
    maxWidth: '180px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

const descriptionStyle: React.CSSProperties = {
    ...truncateStyle,
    maxWidth: '260px',
}

const OccurrencesTable: React.FC<OccurrencesTableProps> = ({ search, appliedFilters = [] }) => {
    const { setModalSection, setModalActive, modalActive } = useContext(ModalContext)
    const { setOccurrence } = useContext(OccurrenceContext)
    const { setFeedback } = useContext(FeedbackContext)
    const [occurrences, setOccurrences] = useState<Occurrence[]>([])
    const [loading, setLoading] = useState(true)
    const [openDropdownId, setOpenDropdownId] = useState<number | null>(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [deletingOccurrence, setDeletingOccurrence] = useState<Occurrence | null>(null)
    const [isDeleting, setIsDeleting] = useState(false)
    const buttonRefs = useRef<{ [key: number]: HTMLButtonElement | null }>({})
    const [currentPage, setCurrentPage] = useState<number>(1)
    const [totalOccurrences, setTotalOccurrences] = useState<number>(0)
    const [hasNextPage, setHasNextPage] = useState<boolean>(false)
    const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false)

    const ITEMS_PER_PAGE = 50

    const loadOccurrences = (page: number = 1, append: boolean = false, searchTerm?: string) => {
        if (append) {
            setIsLoadingMore(true)
        } else {
            setLoading(true)
        }

        ApiService.occurrences.findAll({ page, size: ITEMS_PER_PAGE, search: searchTerm })
            .then(response => {
                const returnedItems: Occurrence[] = response.data.content || []
                const total: number = response.data.totalElements || 0
                const returnedCount = returnedItems.length

                if (append) {
                    setOccurrences(prev => {
                        const existingIds = new Set(prev.map(o => o.id))
                        const newItems = returnedItems.filter(o => !existingIds.has(o.id))
                        const updated = [...prev, ...newItems]
                        const hasMore = returnedCount === ITEMS_PER_PAGE && updated.length < total
                        setHasNextPage(hasMore)
                        return updated
                    })
                } else {
                    setOccurrences(returnedItems)
                    const hasMore = returnedCount === ITEMS_PER_PAGE && returnedCount < total
                    setHasNextPage(hasMore)
                }

                setTotalOccurrences(total)
                setCurrentPage(page)
            })
            .catch(error => {
                console.error(error)
                if (!append) {
                    setOccurrences([])
                    setTotalOccurrences(0)
                    setHasNextPage(false)
                }
            })
            .finally(() => {
                if (append) {
                    setIsLoadingMore(false)
                } else {
                    setLoading(false)
                }
            })
    }

    useEffect(() => {
        loadOccurrences(1, false, search)
    }, [])

    useEffect(() => {
        setCurrentPage(1)
        loadOccurrences(1, false, search)
    }, [search])

    // Reload when modal closes
    useEffect(() => {
        if (!modalActive) {
            loadOccurrences(1, false, search)
        }
    }, [modalActive])

    const filteredOccurrences = useMemo(() => {
        if (!appliedFilters || appliedFilters.length === 0) return occurrences
        return occurrences.filter(occ =>
            appliedFilters.every(filter => matchesFilter(occ, filter))
        )
    }, [occurrences, appliedFilters])

    const columns = ['ID', 'OP', 'Cliente', 'Descrição', 'Alocacão', 'Setor', 'Etapa', 'Relevante', '']

    const handleViewOccurrence = (occurrence: Occurrence) => {
        setOccurrence(occurrence)
        setModalSection('viewOccurrenceModal')
        setModalActive(true)
    }

    const handleEditOccurrence = async (occurrence: Occurrence) => {
        try {
            const response = await ApiService.occurrences.findById(occurrence.id)
            setOccurrence(response.data)
            setModalSection('createEditOccurrenceModal')
            setModalActive(true)
        } catch (error) {
            console.error('Erro ao carregar ocorrência para edição:', error)
            setOccurrence(occurrence)
            setModalSection('createEditOccurrenceModal')
            setModalActive(true)
        }
    }

    const handleDeleteOccurrence = (occurrence: Occurrence) => {
        setDeletingOccurrence(occurrence)
        setShowDeleteModal(true)
    }

    const handleConfirmDelete = async () => {
        if (!deletingOccurrence) return
        try {
            setIsDeleting(true)
            await ApiService.occurrences.delete(deletingOccurrence.id)
            setFeedback({ message: `Ocorrência ${deletingOccurrence.id} excluída com sucesso!`, isOpen: true, statusOk: true })
            setShowDeleteModal(false)
            setDeletingOccurrence(null)
            loadOccurrences(1, false)
        } catch (error: any) {
            console.error('Erro ao excluir ocorrência:', error)
            setFeedback({ message: 'Erro ao excluir ocorrência. Tente novamente.', isOpen: true, statusOk: false })
        } finally {
            setIsDeleting(false)
        }
    }

    const handleCancelDelete = () => {
        setShowDeleteModal(false)
        setDeletingOccurrence(null)
    }

    const handleToggleDropdown = (id: number) => {
        setOpenDropdownId(prev => prev === id ? null : id)
    }

    const handleCloseDropdown = () => {
        setOpenDropdownId(null)
    }

    if (loading) {
        return <p style={{ padding: '40px', textAlign: 'center', color: '#8c98a4' }}>Carregando ocorrências...</p>
    }

    if (occurrences.length === 0) {
        return <p style={{ padding: '40px', textAlign: 'center', color: '#8c98a4' }}>Nenhuma ocorrência encontrada.</p>
    }

    return (
        <>
        <TableWrapper>
            <GlobalTable>
                <thead>
                    <HeaderLine>
                        {columns.map((item, colIndex) => (
                            <Th key={colIndex}>{item}</Th>
                        ))}
                    </HeaderLine>
                </thead>
                <tbody>
                    {filteredOccurrences.length === 0 ? (
                        <Tr>
                            <Td colSpan={columns.length} style={{ textAlign: 'center', color: '#8c98a4' }}>
                                Nenhuma ocorrência encontrada com os filtros aplicados.
                            </Td>
                        </Tr>
                    ) : (
                        filteredOccurrences.map((occurrence) => (
                            <Tr key={occurrence.id}>
                            <Td>{occurrence.id}</Td>
                            <Td style={truncateStyle}>{occurrence.operationNumber ?? 'N/A'}</Td>
                            <Td style={truncateStyle}>{occurrence.companyName || occurrence.contactName || 'N/A'}</Td>
                            <Td style={descriptionStyle}>{occurrence.description ?? 'N/A'}</Td>
                            <Td style={truncateStyle}>{occurrence.allocation ?? 'N/A'}</Td>
                            <Td style={truncateStyle}>{occurrence.sector ?? 'N/A'}</Td>
                            <Td style={truncateStyle}>{occurrence.Stage?.name ?? 'N/A'}</Td>
                            <Td>{occurrence.relevant ? 'Sim' : 'Não'}</Td>
                            <TdButtons>
                                <div style={{ position: 'relative' }}>
                                    <button
                                        ref={(el) => {
                                            buttonRefs.current[occurrence.id] = el
                                        }}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleToggleDropdown(occurrence.id)
                                        }}
                                        style={{
                                            background: 'transparent',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <MoreHorizontal size={18} />
                                    </button>
                                    {openDropdownId === occurrence.id && (
                                        <ActionsDropdown
                                            buttonRef={buttonRefs.current[occurrence.id]}
                                            onView={() => handleViewOccurrence(occurrence)}
                                            onEdit={() => handleEditOccurrence(occurrence)}
                                            onDelete={() => handleDeleteOccurrence(occurrence)}
                                            onClose={handleCloseDropdown}
                                        />
                                    )}
                                </div>
                            </TdButtons>
                        </Tr>
                    ))
                )}
            </tbody>
            <ConfirmationModal
                isOpen={showDeleteModal}
                title="Excluir Ocorrência"
                message={`Tem certeza que deseja excluir a ocorrência ${deletingOccurrence?.id}? Esta ação não pode ser desfeita.`}
                actions={[
                    {
                        label: 'Cancelar',
                        onClick: handleCancelDelete,
                        variant: 'outline'
                    },
                    {
                        label: isDeleting ? 'Excluindo...' : 'Excluir',
                        onClick: handleConfirmDelete,
                        variant: 'danger'
                    }
                ]}
            />
        </GlobalTable>
        <LoadMoreButton
            onLoadMore={() => loadOccurrences(currentPage + 1, true)}
            isLoading={isLoadingMore}
            hasMore={hasNextPage}
            currentCount={occurrences.length}
            totalCount={totalOccurrences}
        />
        </TableWrapper>
        </>
    )
}

export default OccurrencesTable
